<template>
  <div class="w-full max-w-[500px]">
    <PhotoPicker
      :title="question.questionTitle"
      :description="question.question"
      :example-image-src="question.exampleImageSrc"
      :error-message="errorMessage"
      :image-preview-init="question.imagePreview"
      :image-file-init="question.imagePreview"
      @file-state-change="fileStateChange"
      @preview-state-change="previewStateChange"
      @failure="failedEvent"
      @click-upload="onClickUpload"
      @click-change="onClickChange"
    />

    <NextQuestionButton
      v-if="imagePreview"
      :image-preview="imagePreview"
      :loading="submitting"
      @click="uploadImageToServer"
    />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import _ from "lodash";
import axios from "axios";

import NextQuestionButton from "@/Consultation/components/NextQuestionButton.vue";
import PhotoPicker from "@/ComponentLibrary/FileUpload/PhotoPicker.vue";
import { PhotoUploadQuestion } from "@/Consultation/types";
import QuestionnaireSubmitter from "@/Consultation/mixins/poster";
import { amplitudeEvent } from "@/MXPixel/MedexpressAmplitude";

interface PhotoUploadQuestionData {
  imagePreview: string | ArrayBuffer | null;
  submitting: boolean;
  imageFile: File | null;
  errorMessage: string | null;
}

export default Vue.extend(QuestionnaireSubmitter).extend({
  name: "PhotoUploadQuestion",
  inject: ["submitUrl", "csrfHeader", "csrf"],
  components: { NextQuestionButton, PhotoPicker },
  mounted() {
    this.validate();
    amplitudeEvent("View Photo Upload Question", {
      questionId: this.question.id,
    });
  },
  destroyed() {
    amplitudeEvent("Left Photo Upload Question", {
      questionId: this.question.id,
    });
  },
  props: {
    question: {
      type: Object as PropType<PhotoUploadQuestion>,
      required: true,
    },
  },
  data(): PhotoUploadQuestionData {
    return {
      imagePreview: this.question.imagePreview ?? null,
      imageFile: this.question.imageFile ?? null,
      submitting: false,
      errorMessage: null,
    };
  },
  methods: {
    async uploadImageToServer() {
      if (_.isNull(this.imageFile)) {
        return;
      }

      this.submitting = true;
      const formData = new FormData();
      formData.append("photo", this.imageFile);

      try {
        const response = await axios
          .post("/rest/user/photo/hair-loss", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              [this.csrfHeader]: this.csrf,
            },
          })
          .then((r) => {
            const { imageKey } = r.data;
            this.question.responseImageKey = imageKey;
            this.errorMessage = null;
            this.post({ responseImageKey: imageKey });
            this.validate();
            amplitudeEvent("Proceed Photo Upload Question", {
              questionId: this.question.id,
            });
            this.$emit("next");
          })
          .catch((error) => {
            const message = error?.response?.data?.message || error;
            this.errorMessage = message;
            this.failedEvent(message);
          });
      } finally {
        this.submitting = false;
      }
    },
    fileStateChange(file: File | null) {
      this.imageFile = file;
    },
    previewStateChange(preview: string | ArrayBuffer | null) {
      this.imagePreview = preview;
      if (preview != null) {
        amplitudeEvent("Photo Uploaded on Photo Upload Question", {
          questionId: this.question.id,
        });
      }
    },
    failedEvent(errorMessage: string): void {
      amplitudeEvent("Failed Photo Upload Question", {
        questionId: this.question.id,
        errorMessage: errorMessage,
      });
    },
    onClickUpload(): void {
      amplitudeEvent("Click Upload on Photo Upload Question", {
        questionId: this.question.id,
      });
    },
    onClickChange(): void {
      amplitudeEvent("Click Change on Photo Upload Question", {
        questionId: this.question.id,
      });
    },
    validate() {
      const hasExistingFile = !!this.question.imagePreview && !!this.question.imageFile;
      this.$emit("validation", this.question.id, hasExistingFile);
    },
  },
  watch: {
    imagePreview(newValue) {
      this.question.imagePreview = newValue;
    },
    imageFile(newValue) {
      this.question.imageFile = newValue;
    },
  },
});
</script>
